import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  CriteriaQuota,
  FeasibilityRangeQuota,
  FeasibilityRequest,
  FeasibilityResponse,
} from '../../../shared/interfaces/feasibility.interface';
import {
  Criteria,
  Flexible,
  Qualification,
  QualificationData,
} from '../../../shared/interfaces/qualification.interface';
import { CommonService } from '../../../shared/services/common-service/common-service';
import {
  EachSupplier,
  Qualification as SurveyQUalification,
  SupplierResponseInterface,
  SurveyRequest,
} from '../../../shared/interfaces/survey.interface';
import { QUALIFICATION_CODES } from '../../../shared/constants/qualification-code';
import { DEFAULT_AGE_QUOTA_CRITERIA } from '../../constants/default-age-genpop-quota-criteria';
import { BuyerApiService } from '../../../shared/services/buyer-api/survey.service';
import { QUALIFICATIONS_CATEGORIES } from '../../../shared/constants/qualification-categories';
import { QUALIFICATION_NAMES } from '../../constants/qualification-name';
import { EnvironmentConfig } from '../../../shared/interfaces/environment.interface';

@Injectable({
  providedIn: 'root',
})
export class FeasibilityService {
  private readonly _baseUrl = this._env.environment.pureSpectrum.feasibilityUrl;
  constructor(
    private _http: HttpClient,
    private _commonService: CommonService,
    private _buyerApiService: BuyerApiService,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {}

  getFeasibility(request: FeasibilityRequest) {
    return this._http.post<FeasibilityResponse>(
      `${this._baseUrl}/feasibility/v2?summary=2&UI=1`,
      request
    );
  }

  private _createQuota(
    criteria: Criteria[],
    required_count: number,
    flexible?: Flexible,
    buyer_quota_id: string = this._commonService.getRandomString()
  ): FeasibilityRangeQuota {
    return {
      buyer_quota_id,
      required_count,
      criteria,
      flexible,
    };
  }

  getAgeQualificationData(qualifications: Qualification[]): QualificationData {
    const ageQualification = qualifications?.find(
      (q) => q.qualification_code === QUALIFICATION_CODES.AGE
    );
    if (!ageQualification) {
      return {};
    }
    return ageQualification.data[0];
  }

  async getSuppliers(
    surveyId: number,
    expected_loi: number,
    expected_ir: number,
    languageCode: string,
    field_time: number,
    completes_required?: number
  ) {
    return await this._buyerApiService
      .getSuppliers(
        surveyId,
        'editable',
        expected_loi,
        expected_ir,
        languageCode,
        field_time,
        completes_required
      )
      .toPromise();
  }

  mapSupplierIds(suppliers: SupplierResponseInterface[]): number[] {
    // the frontend_platform code filter by isChecked, verify later if
    // using is_active as filter is valid
    return suppliers
      .reduce(
        (arr: EachSupplier[], curr) =>
          curr.is_active && curr.isChecked
            ? arr.concat(
                curr.suppliers.filter(
                  (s) => !arr.some((sa) => sa.id === s.id)
                ) as any[]
              )
            : arr,
        []
      )
      .map((s) => s.id);
  }

  setDbQualificationCodeForQuotas(
    qualifications?: SurveyQUalification[],
    quotas?: FeasibilityRangeQuota[]
  ) {
    quotas?.forEach((quota) => {
      quota.criteria.forEach((crit) => {
        crit.db_qualification_code = qualifications
          ?.filter(
            (qual) => qual.qualification_code === crit.qualification_code
          )
          .map((qual) => qual.db_qualification_code)[0];
      });
    });
  }

  private _validadeIncomeForFeas(
    surveyRequest: SurveyRequest,
    qualifications: Qualification[]
  ) {
    const qualificationIndex = qualifications.findIndex(
      (quali) => quali.db_qualification_code === QUALIFICATION_CODES.INCOME
    );

    const incomeIndex = surveyRequest.qualifications?.findIndex(
      (qualification) =>
        qualification.qualification_code === QUALIFICATION_CODES.INCOME
    );

    if (incomeIndex !== undefined && incomeIndex >= 0) {
      if (qualificationIndex !== undefined && qualificationIndex >= 0) {
        const maxIncomeValue =
          qualifications[qualificationIndex].data[0].maxPlaceholder;
        const minIncomeValue =
          qualifications[qualificationIndex].data[0].minPlaceholder;

        if (
          surveyRequest.qualifications![incomeIndex].range_sets[0].from ==
            minIncomeValue &&
          surveyRequest.qualifications![incomeIndex].range_sets[0].to ==
            maxIncomeValue
        ) {
          const quotaValidation = surveyRequest.quotas?.some((quota) =>
            quota.criteria.some(
              (crit) => crit.qualification_code === QUALIFICATION_CODES.INCOME
            )
          );
          if (quotaValidation !== true) {
            surveyRequest.qualifications!.splice(incomeIndex, 1);
          }
        }
      }
    }
  }

  buildFeasibilityPayload(
    qualifications: Qualification[],
    surveyRequest: SurveyRequest,
    surveyId: number | undefined,
    supplier_ids: number[],
    survey_grouping?: number[],
    is_exclusion?: boolean
  ) {
    if (qualifications.length !== 0) {
      this._validadeIncomeForFeas(surveyRequest, qualifications);
    }

    let {
      survey_localization,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      buyer_company: company,
      buyer_user_id: user_id,
      quotas: payloadQuotas,
      qualifications: payloadQualifications,
      click_balancing,
      estimated_clicks,
    } = surveyRequest;

    const quotaTargetedQualification = [
      ...new Set(
        payloadQuotas?.flatMap(({ criteria }) =>
          criteria.map(({ qualification_code }) => qualification_code)
        )
      ),
    ];
    // set db_qualification_code for quotas
    this.setDbQualificationCodeForQuotas(payloadQualifications, payloadQuotas);

    // create required default age quota
    const defaultAgeQuota: Criteria = {
      qualification_code: QUALIFICATION_CODES.AGE,
      qualification_name: QUALIFICATION_NAMES.AGE,
      range_sets: [
        {
          from: DEFAULT_AGE_QUOTA_CRITERIA.MIN,
          to: DEFAULT_AGE_QUOTA_CRITERIA.MAX,
          units: this._commonService.getUnit(QUALIFICATION_CODES.AGE),
        },
      ],
    };

    payloadQualifications = payloadQualifications?.filter(
      (payloadQualification) => {
        if (payloadQualification.hasOwnProperty('isDefault')) {
          return !payloadQualification.isDefault;
        } else if (
          payloadQualification.hasOwnProperty('qualification_name') &&
          payloadQualification.qualification_name === 'Location'
        ) {
          return false;
        }
        return true;
      }
    );

    const defaultQuotaCriteria: Criteria[] = payloadQualifications || [];
    const quotas: FeasibilityRangeQuota[] = [];
    // age quota must always go in default quotas
    if (
      !defaultQuotaCriteria.some(
        (c) => c.qualification_code === QUALIFICATION_CODES.AGE
      )
    ) {
      defaultQuotaCriteria.push(defaultAgeQuota);
    }

    // deal with quotas
    const ageQuota = this._createQuota(
      defaultQuotaCriteria,
      completes_required
    );
    const quotaSet: Map<number, CriteriaQuota[]> = new Map();
    const feasibilityQuota = payloadQuotas?.length ? payloadQuotas : [ageQuota];
    feasibilityQuota.forEach((q: FeasibilityRangeQuota) => {
      const definedCriteria = defaultQuotaCriteria.slice();
      const quotasCriteria: Criteria[] = this._resolveDefinedCriteria(
        definedCriteria,
        q.criteria
      );

      // if is default quota set the qualification as AGE always
      // otherwise get first criteria
      let idxCriteria = 0;
      if (!payloadQuotas) {
        idxCriteria = quotasCriteria.findIndex(
          (qq) => qq.qualification_code === QUALIFICATION_CODES.AGE
        );
      }

      const qualification = q.criteria[idxCriteria].qualification_code;
      const idx = quotasCriteria.findIndex(
        (c) => c.qualification_code === qualification
      );
      quotasCriteria[idx] = q.criteria[idxCriteria];
      quotasCriteria?.map((criteria) => {
        criteria.non_quota_targeted = quotaTargetedQualification.includes(
          criteria.qualification_code
        )
          ? false
          : true;
      });
      quotas.push({
        ...this._createQuota(
          quotasCriteria,
          q.required_count,
          q.flexible,
          q.buyer_quota_id
        ),
        quota_type: this._getQuotaType(q, idxCriteria),
      });

      // join quotas for less complex iterations when displaying
      const criteriaQuota: CriteriaQuota = {
        ...q,
        ...q.criteria[idxCriteria],
      };

      const quotaSameQualification = quotaSet.get(qualification);
      if (quotaSameQualification) {
        quotaSameQualification.push(criteriaQuota);
      } else {
        quotaSet.set(qualification, [criteriaQuota]);
      }
    });

    const request: FeasibilityRequest = {
      survey_localization,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      user_id,
      buyer_id: company,
      project_id: Number(surveyId),
      quotas,
      defaultQuota: [
        {
          criteria: defaultQuotaCriteria,
        },
      ],
      survey_grouping,
      is_exclusion,
      estimated_clicks,
      click_balancing,
    };
    if (supplier_ids?.length) {
      request.supplier_ids = supplier_ids;
    }
    request.quotas = this.mapQualificationCodeForQuotas(quotas);
    request.defaultQuota[0].criteria =
      this.mapQualCodeOfCrit(defaultQuotaCriteria);
    return { request, defaultQuotaCriteria, quotaSet };
  }

  /**
   * Find defined criteria in quota criteria to prevent nested to have multiples conditions on payload
   */
  private _resolveDefinedCriteria(
    definedCriteria: Criteria[],
    quotaCriteria: Criteria[]
  ): Criteria[] {
    const criteria = new Map<number, Criteria>();

    definedCriteria.forEach((defined) =>
      criteria.set(defined.qualification_code, defined)
    );

    quotaCriteria.forEach((qCriteria) => {
      criteria.set(qCriteria.qualification_code, qCriteria);
    });

    return [...criteria.values()];
  }

  private _getQuotaType(quota: FeasibilityRangeQuota, index: number) {
    if (quota.criteria.length === 1) {
      return quota.criteria[index].qualification_code.toString();
    } else if (
      quota.criteria.length === 3 &&
      [
        QUALIFICATION_CODES.CHILDREN,
        QUALIFICATION_CODES.CHILD_AGE,
        QUALIFICATION_CODES.CHILD_GENDER,
      ].every((code) =>
        quota.criteria.some((q) => q.qualification_code === code)
      )
    ) {
      return quota.criteria[index].qualification_code.toString();
    } else {
      return QUALIFICATIONS_CATEGORIES.NESTED;
    }
  }

  mapQualificationCodeForQuotas(quotas: FeasibilityRangeQuota[]) {
    const quotasForFeasibility: FeasibilityRangeQuota[] = [];
    quotas.forEach((quota) => {
      quotasForFeasibility.push({
        buyer_quota_id: quota.buyer_quota_id,
        criteria: this.mapQualCodeOfCrit(quota.criteria),
        required_count: quota.required_count,
        flexible: quota.flexible,
        quota_type: quota.quota_type,
      });
    });
    return quotasForFeasibility;
  }

  mapQualCodeOfCrit(criterias: Criteria[]) {
    const quotaCriteriaForFeasibility: Criteria[] = [];
    criterias?.forEach((crit) => {
      quotaCriteriaForFeasibility.push({
        qualification_code:
          crit.db_qualification_code || crit.qualification_code,
        qualification_name: crit.qualification_name,
        range_sets: crit.range_sets,
        condition_codes: crit.condition_codes,
        condition_names: crit.condition_names,
        group_name: crit.group_name,
        non_quota_targeted: crit.non_quota_targeted,
      });
    });
    return quotaCriteriaForFeasibility;
  }

  /*
   * As empirically observed in server responses the quotas[x].count always
   * brings the exact sum of quota suppliers count (quotas[x].suppliers[x].count).
   * Based on that we just sum the quotas[x].count and use it if it's less than max_feasibility.
   */
  calculateTotalFeasibility({ quotas, max_feasibility }: FeasibilityResponse) {
    if (!quotas?.length) {
      return max_feasibility;
    }
    const totalFeasibility = quotas.reduce(
      (prev, curr) => prev + curr.count,
      0
    );
    return totalFeasibility < max_feasibility
      ? totalFeasibility
      : max_feasibility;
  }
}
